/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import styles from './ErrorScreen.style';

import messages from 'src/shared/app/base/l10n/errorL10n';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { supportRoute } from 'src/shared/app/base/route/setting/routeSettings';
import LazySupportMain from 'src/shared/app/support/component/LazySupportMain';
import PrimaryButton from 'src/shared/ui/component/button/PrimaryButton';
import DiscreteLink from 'src/shared/ui/component/link/DiscreteLink';
import Subheading from 'src/shared/ui/component/text/Subheading';

type Props = {
  sentryId: ?string,
  error: Error,
  errorInfo: Object,
  isErrorReportingEnabled: boolean,
};

const useStyles = createUseStyles(styles);

const ErrorScreenDumb = ({
  sentryId,
  error,
  errorInfo,
  isErrorReportingEnabled,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const [supportPath] = useRoutes(supportRoute);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onRetry = () => window.location.reload();

  const onMouseOver = () => LazySupportMain.preload();

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const detailsElt =
    isErrorReportingEnabled && sentryId ? (
      <p className={classes.ref}>
        <span className={classes.refLabel}>
          {intl.formatMessage(messages.errorId)}
        </span>
        <br />
        <span className={classes.refId}>{sentryId}</span>
      </p>
    ) : (
      <div className={classes.message}>
        <p>{error.message}</p>
        {errorInfo.componentStack.split('\n').map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
    );

  return (
    <section className={classes.root}>
      <Subheading>{intl.formatMessage(messages.genericError)}</Subheading>
      <div className={classes.actions}>
        <PrimaryButton layoutClass={classes.retry} handleClick={onRetry}>
          {intl.formatMessage(messages.retry)}
        </PrimaryButton>
        <DiscreteLink href={supportPath} handleMouseOver={onMouseOver}>
          {intl.formatMessage(messages.contact)}
        </DiscreteLink>
      </div>
      <div className={classes.details}>{detailsElt}</div>
    </section>
  );
};

export default compose(React.memo)(ErrorScreenDumb);
